import { useState, useEffect, useRef, useCallback } from "react";
import { BACKEND_URL } from "@/utils/constants";

export const useWebSocket = (user, onEvent) => {
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [editingUsers, setEditingUsers] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);

  const connect = useCallback(() => {
    if (!user || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/api/ws?user_id=${encodeURIComponent(user.user_id)}&name=${encodeURIComponent(user.name || user.email)}&picture=${encodeURIComponent(user.picture || '')}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Heartbeat every 30s
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === "presence") {
          setOnlineUsers(msg.users || []);
        } else if (msg.type === "event") {
          if (onEvent) onEvent(msg);
        } else if (msg.type === "editing") {
          setEditingUsers(prev => {
            const key = `${msg.entity_type}:${msg.entity_id}`;
            if (msg.is_editing) {
              return { ...prev, [key]: { user_id: msg.user_id, user_name: msg.user_name } };
            } else {
              const next = { ...prev };
              delete next[key];
              return next;
            }
          });
        } else if (msg.type === "chat_message") {
          window.dispatchEvent(new Event("chat-message-received"));
        }
      } catch {}
    };

    ws.onclose = () => {
      setConnected(false);
      clearInterval(pingIntervalRef.current);
      // Reconnect after 3s
      reconnectTimeoutRef.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [user, onEvent]);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  const sendEditing = useCallback((entityType, entityId, isEditing) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "editing",
        entity_type: entityType,
        entity_id: entityId,
        is_editing: isEditing
      }));
    }
  }, []);

  const sendViewing = useCallback((view) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "viewing", view }));
    }
  }, []);

  return { connected, onlineUsers, editingUsers, sendEditing, sendViewing };
};
